import { useEffect, useState } from "react";
import {
  adminGetFieldNotes, adminUpsertFieldNote, adminDeleteFieldNote, formatApiErrorDetail,
} from "@/lib/api";
import { Plus, Trash2, Save, X, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const SOURCES = ["original", "linkedin", "newsletter"];

const EMPTY = {
  title: "", excerpt: "", author: "Copper & Vine", body: "",
  tags: "", source: "original", canonical_url: "", status: "published",
  order: 99, published_at: "",
};

function isoToDateInput(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}
function dateInputToIso(d) {
  if (!d) return null;
  try { return new Date(`${d}T00:00:00Z`).toISOString(); } catch { return null; }
}
function toForm(n) {
  return {
    ...n,
    tags: Array.isArray(n.tags) ? n.tags.join(", ") : (n.tags || ""),
    published_at: isoToDateInput(n.published_at),
  };
}
function toPayload(form) {
  return {
    ...form,
    tags: String(form.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
    published_at: dateInputToIso(form.published_at),
  };
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

export default function AdminFieldNotes() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems(await adminGetFieldNotes()); }
    catch { toast.error("Could not load field notes."); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY, published_at: isoToDateInput(new Date().toISOString()) });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required."); return; }
    setBusy(true);
    try {
      const res = await adminUpsertFieldNote(toPayload(editing));
      toast.success("Saved. The Resources page updates immediately.");
      setEditing((e) => ({ ...e, id: res.id, slug: res.slug }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const togglePublished = async (n) => {
    try {
      await adminUpsertFieldNote({
        ...n,
        tags: Array.isArray(n.tags) ? n.tags : [],
        status: n.status === "published" ? "draft" : "published",
      });
      toast.success(n.status === "published" ? "Moved to draft." : "Published.");
      load();
    } catch { toast.error("Could not update."); }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"? This cannot be undone.`)) return;
    try { await adminDeleteFieldNote(n.id); toast.success("Deleted."); load(); }
    catch { toast.error("Could not delete."); }
  };

  return (
    <div data-testid="admin-field-notes">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Field Notes</p>
          <h1 className="cv-display text-3xl mt-4">The writing, fully editable.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-lg">
            Long-form articles in Markdown. Set the author and publish date, draft or publish, and the public library sorts newest first.
          </p>
        </div>
        <button data-testid="field-note-new-btn" onClick={startNew}
          className="cv-btn-primary inline-flex items-center gap-2 self-start whitespace-nowrap">
          <Plus size={15} /> New field note
        </button>
      </div>

      {editing && (
        <FieldNoteForm value={editing} onChange={setEditing} onSave={save} onCancel={cancel} busy={busy} />
      )}

      <div className="mt-8 divide-y divide-[rgba(28,27,25,0.12)] border-t border-[rgba(28,27,25,0.12)]">
        {items.map((n) => (
          <div key={n.id} data-testid="admin-field-note-row" className="py-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="cv-h2 text-sm">{n.title}</p>
                {n.canonical_url && (
                  <span className="inline-flex items-center gap-1 text-[#515E4F]" title="Has external canonical link"><ExternalLink size={12} /></span>
                )}
              </div>
              <p className="cv-body text-xs text-[#9A938A] mt-0.5 truncate">
                {n.author} · {fmtDate(n.published_at)}{n.tags?.length ? ` · ${(Array.isArray(n.tags) ? n.tags : []).join(", ")}` : ""}
              </p>
            </div>
            <button data-testid="field-note-publish-toggle" onClick={() => togglePublished(n)}
              className={`inline-flex items-center gap-1.5 cv-sans uppercase tracking-[0.1em] text-[0.6rem] px-2.5 py-1 rounded-full border transition-colors ${
                n.status === "published" ? "border-[#515E4F] text-[#515E4F]" : "border-[rgba(28,27,25,0.3)] text-[#9A938A]"
              }`}>
              {n.status === "published" ? <Eye size={12} /> : <EyeOff size={12} />}
              {n.status === "published" ? "Published" : "Draft"}
            </button>
            <button onClick={() => setEditing(toForm(n))} className="cv-link text-xs">Edit</button>
            <button onClick={() => remove(n)} className="text-[#A65A43] hover:text-[#8a4836]" aria-label="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="cv-body text-sm text-[#9A938A] py-6">No field notes yet.</p>
        )}
      </div>
    </div>
  );
}

function FieldNoteForm({ value, onChange, onSave, onCancel, busy }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div data-testid="field-note-form" className="mt-8 border border-[rgba(28,27,25,0.22)] rounded-[16px] overflow-hidden">
      <div className="cv-hairline-copper" />
      <div className="p-6 space-y-4">
        <Field label="Title *">
          <input data-testid="field-note-title" className="cv-input"
            value={value.title} onChange={(e) => set("title", e.target.value)} />
        </Field>
        <Field label="Excerpt (shown on the card)">
          <textarea data-testid="field-note-excerpt" rows={2} className="cv-input resize-none"
            value={value.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Author">
            <input data-testid="field-note-author" className="cv-input"
              value={value.author} onChange={(e) => set("author", e.target.value)} />
          </Field>
          <Field label="Publish date">
            <input data-testid="field-note-date" type="date" className="cv-input"
              value={value.published_at} onChange={(e) => set("published_at", e.target.value)} />
          </Field>
          <Field label="Source">
            <select data-testid="field-note-source" className="cv-input"
              value={value.source} onChange={(e) => set("source", e.target.value)}>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Tags (comma separated)">
            <input data-testid="field-note-tags" className="cv-input" placeholder="build-buy-fix, governance"
              value={value.tags} onChange={(e) => set("tags", e.target.value)} />
          </Field>
          <Field label="Canonical URL (optional; card links out when set)">
            <input data-testid="field-note-canonical" className="cv-input" placeholder="https://..."
              value={value.canonical_url} onChange={(e) => set("canonical_url", e.target.value)} />
          </Field>
        </div>
        <Field label="Body (Markdown: ## headings, - lists, [text](/path) links, **bold**)">
          <textarea data-testid="field-note-body" rows={12} className="cv-input resize-none font-mono text-sm"
            value={value.body} onChange={(e) => set("body", e.target.value)} />
        </Field>

        <div className="flex items-center gap-3 pt-1">
          <button data-testid="field-note-published-toggle" type="button"
            onClick={() => set("status", value.status === "published" ? "draft" : "published")}
            className="inline-flex items-center gap-2.5">
            <span className={`w-9 h-5 rounded-full border transition-colors relative ${value.status === "published" ? "bg-[#A6633C] border-[#A6633C]" : "bg-transparent border-[rgba(28,27,25,0.3)]"}`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${value.status === "published" ? "left-4 bg-[#F6F1E9]" : "left-0.5 bg-[#9A938A]"}`} />
            </span>
            <span className="cv-sans uppercase tracking-[0.1em] text-[0.68rem] text-[#1F1B16]/70">Published</span>
          </button>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button data-testid="field-note-save" onClick={onSave} disabled={busy}
            className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={14} /> {busy ? "Saving..." : "Save field note"}
          </button>
          <button onClick={onCancel} className="cv-btn-secondary inline-flex items-center gap-2">
            <X size={14} /> Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
